import { isAnnotationBundleSet } from "@/api/trainingAnnotation";
import { AnnotationBundleImportModal } from "./import/AnnotationBundleImportModal";
import {
  defaultTextVariants,
  IconButton,
  Menu,
  SingleSelectDropdown,
  Spinner,
  Text,
  toast,
} from "@carbon6/c6-frontend-design";
import {
  CopyIcon,
  DownloadIcon,
  EditIcon,
  PlusIcon,
  RotateCcwIcon,
  TrashIcon,
} from "@carbon6/c6-frontend-design/icons";
import { useMemo, useState, type ReactElement } from "react";
import type { AnnotationSetSummaryView } from "@/api/trainingAnnotation";
import { hatcheryDropdownModes } from "@/app/theme";
import { pickDownloadDirectory, supportsDirectoryDownload } from "@/components/dataset/download";
import {
  annotationSetBlockReason,
  annotationSetLabel,
} from "@/page/training/submit/annotationSetSelectable";
import type { AnnotationSetDownloadController } from "./download/useAnnotationSetDownload";
import { AnnotationSetNameModal } from "./AnnotationSetNameModal";
import { HatTextModal } from "@/components/modal";
import { HatTooltip } from "@/components/tooltip";
import type { AnnotationStudioBinding } from "./useAnnotationStudio";
import { EmptyState } from "@/shared/ui/emptyState/public";
import { inlineSpinnerClassName } from "@/shared/ui/loadingState/public";
import styles from "./AnnotationSetPanel.module.css";

type PanelDialog = "create" | "rename" | "delete" | "import";

/**
 * 카메라·에피소드 탐색보다 앞에 두는 저장 범위 선택 영역.
 *
 * 제목 옆에 작업 버튼을 두고 드롭다운은 좌측 패널의 전체 폭을 사용한다.
 * 새 세트는 드롭다운 검색 줄과 작업 메뉴에서 만든다. 이름 변경·복제·삭제는 작업 메뉴에서
 * 명시적으로 실행한다. 확정 세트의 잠금 사유는 도구 위 오버레이(`AnnotationSetLockOverlay`)가
 * 표시한다.
 *
 * `gpuLocked` 이면 세트 선택과 다운로드만 남긴다. 만들기·확정·가져오기는 이어서 GPU holder 가
 * 필요하다. 그래서 잠금 카드가 사라질 때까지 열지 않는다.
 */
export function AnnotationSetPanel({
  binding,
  gpuLocked = false,
}: {
  binding: AnnotationStudioBinding;
  gpuLocked?: boolean;
}) {
  const { selectedSet, setAction } = binding;
  const [dialog, setDialog] = useState<PanelDialog | null>(null);
  const options = useMemo(
    () =>
      binding.sets.sets.map((set) => ({
        value: set.uuid,
        // 트리거에는 이름만 표시한다. 좁은 줄에 상태를 덧붙이면 이름이 먼저 잘려 세트를
        // 구분할 수 없다. 상태는 폭이 넓은 메뉴 항목의 설명 줄에 표시한다.
        label: annotationSetLabel(set),
        description: isAnnotationBundleSet(set)
          ? `${set.origin === "upload_bundle" ? "업로드 번들" : "가져온 번들"} · keyframe ${set.bundle?.keyframe_count ?? set.annotated_frame_count}개`
          : `${set.status === "draft" ? "작성 중" : "확정됨"}${frameSummary(set)}`,
      })),
    [binding.sets.sets],
  );

  const busy = setAction.action !== null;
  const listLoading = binding.sets.status === "loading";
  const materialize = selectedSet?.materialize;
  const draft = selectedSet?.status === "draft";
  const bundle = isAnnotationBundleSet(selectedSet);
  const createDisabledReason = binding.canCreateSet
    ? undefined
    : binding.snapshot.isDirty
      ? "현재 세트의 변경을 저장하거나 취소한 뒤 만들 수 있습니다."
      : "진행 중인 작업이 끝난 뒤 만들 수 있습니다.";
  const downloadBlock =
    selectedSet === null ? null : downloadBlockReason(selectedSet, binding.download.active);

  return (
    <section className={styles.panel} aria-label="Annotation Set">
      <div className={styles.header}>
        <Text variant={defaultTextVariants["caption-bold"]}>Annotation Set</Text>
        {draft && !gpuLocked ? (
          <IconButton
            text={setAction.action === "commit" ? "확정 중…" : "확정"}
            size="xs"
            variant="solid"
            disabled={busy || selectedSet.annotated_frame_count === 0}
            onClick={() => void binding.commitSelectedSet()}
          />
        ) : null}
        <Menu label="Annotation Set 작업">
          <Menu.Trigger label="세트 작업 더 보기" size="xs" disabled={busy} />
          <Menu.Item
            hidden={gpuLocked}
            icon={<PlusIcon />}
            disabled={!binding.canCreateSet}
            caption={createDisabledReason}
            onSelect={() => setDialog("create")}
          >
            새 Annotation Set
          </Menu.Item>
          <Menu.Item
            hidden={gpuLocked}
            icon={<PlusIcon />}
            disabled={!binding.canCreateSet}
            caption={createDisabledReason}
            onSelect={() => setDialog("import")}
          >
            파일에서 가져오기
          </Menu.Item>
          {gpuLocked ? null : <Menu.Separator />}
          <Menu.Item
            hidden={gpuLocked}
            disabled={selectedSet === null}
            icon={<EditIcon />}
            onSelect={() => setDialog("rename")}
          >
            이름 변경
          </Menu.Item>
          <Menu.Item
            icon={<CopyIcon />}
            hidden={gpuLocked || bundle}
            disabled={draft || selectedSet === null || !binding.canCreateSet}
            caption={draft ? "확정한 세트만 복제할 수 있습니다." : undefined}
            onSelect={() => void binding.cloneSelectedSet()}
          >
            새 draft로 복제
          </Menu.Item>
          <Menu.Item
            hidden={gpuLocked || bundle || materialize?.status !== "failed"}
            icon={<RotateCcwIcon />}
            onSelect={() => void binding.retrySelectedSetMaterialize()}
          >
            keyframe 렌더 재시도
          </Menu.Item>
          <Menu.Item
            disabled={selectedSet === null || downloadBlock !== null}
            icon={<DownloadIcon />}
            caption={downloadBlock ?? undefined}
            onSelect={() => {
              if (selectedSet === null) return;
              const target = { uuid: selectedSet.uuid, label: annotationSetLabel(selectedSet) };
              // 폴더 선택 창은 클릭 처리 안에서 바로 연다. await 뒤로 넘기면 사용자 활성화가 사라진다.
              void pickDownloadDirectory()
                .then((directory) => {
                  if (directory !== null) binding.download.start(target, directory);
                })
                .catch(() => {
                  toast.error("저장할 폴더를 열지 못했습니다. 권한을 확인해 주세요.");
                });
            }}
          >
            다운로드
          </Menu.Item>
          {gpuLocked ? null : <Menu.Separator />}
          <Menu.Item
            hidden={gpuLocked}
            disabled={selectedSet === null}
            tone="danger"
            icon={<TrashIcon />}
            onSelect={() => setDialog("delete")}
          >
            삭제
          </Menu.Item>
        </Menu>
      </div>
      <SingleSelectDropdown
        mode={hatcheryDropdownModes.default}
        size="sm"
        label="Annotation Set"
        placeholder={listLoading ? "불러오는 중" : "작업할 세트 선택"}
        value={selectedSet?.uuid ?? null}
        options={options}
        fullWidth
        searchable
        searchPlaceholder="세트 검색"
        searchAction={
          gpuLocked ? undefined : createDisabledReason === undefined ? (
            // c6 는 최상위 요소의 click 에 팝업 닫기를 붙인다. 버튼을 감싸지 않아야 모달이 열릴 때
            // 팝업이 함께 닫힌다.
            <IconButton
              size="sm"
              variant="surface"
              text="새 Annotation Set"
              onClick={() => setDialog("create")}
            >
              <PlusIcon aria-hidden="true" />
            </IconButton>
          ) : (
            <HatTooltip content={createDisabledReason} placement="bottom" align="end">
              <span className={styles.disabledCreate}>
                <IconButton size="sm" variant="surface" text="새 Annotation Set" disabled>
                  <PlusIcon aria-hidden="true" />
                </IconButton>
              </span>
            </HatTooltip>
          )
        }
        emptyMessage={
          // 목록을 받지 못했으면 "없습니다" 로 알리지 않는다. 다시 받는 버튼은 아래 상태 줄에 있다.
          binding.sets.status === "error"
            ? "Annotation Set 목록을 불러오지 못했습니다. 아래 [다시 시도] 로 다시 받아 주세요."
            : "아직 만든 Annotation Set이 없습니다."
        }
        disabled={busy || listLoading}
        onValueChange={(value) => void binding.selectSet(value)}
      />

      {selectedSet === null ? (
        gpuLocked ? null : (
          <span className={styles.status}>작업할 세트를 선택하거나 새로 만들어 주세요.</span>
        )
      ) : draft ? (
        <span className={styles.status}>모든 카메라의 작업을 이 세트에 저장합니다.</span>
      ) : null}

      <AnnotationSetStatus binding={binding} />

      {dialog === "import" && (
        <AnnotationBundleImportModal
          datasetUuid={binding.importContext.datasetUuid}
          cameras={binding.importContext.cameras}
          onClose={() => setDialog(null)}
          onImported={async (setUuid) => {
            await binding.refreshSets();
            return binding.selectSet(setUuid);
          }}
        />
      )}

      <AnnotationSetNameModal
        open={dialog === "create"}
        label="새 Annotation Set을 만듭니다."
        caption="한 Training에 사용할 여러 카메라의 Annotation은 같은 세트에 저장하세요. 별도 세트가 필요할 때만 새로 만드세요."
        okText="만들기"
        busy={setAction.action === "create"}
        onSubmit={(name) => {
          void binding.createSet(name).then((created) => {
            if (created) setDialog(null);
          });
        }}
        onCancel={() => setDialog(null)}
      />
      <AnnotationSetNameModal
        open={dialog === "rename"}
        label="Annotation Set 이름을 바꿉니다."
        caption="표시 이름만 바뀝니다. 저장한 Annotation은 그대로입니다."
        okText="저장"
        defaultName={selectedSet?.name ?? ""}
        busy={setAction.action === "rename"}
        onSubmit={(name) => {
          void binding.renameSelectedSet(name).then((renamed) => {
            if (renamed) setDialog(null);
          });
        }}
        onCancel={() => setDialog(null)}
      />
      <HatTextModal
        open={dialog === "delete"}
        label="Annotation Set을 삭제할까요?"
        caption="삭제한 세트는 목록에서 사라집니다."
        okText="삭제"
        error
        busy={setAction.action === "delete"}
        onOkPress={() => {
          void binding.deleteSelectedSet().then((deleted) => {
            if (deleted) setDialog(null);
          });
        }}
        onCancelPress={() => setDialog(null)}
      />
    </section>
  );
}

/** 표시할 내용이 있을 때만 추가하는 한 줄. 진행 중인 렌더링, 적재, 실패 사유를 표시한다. */
function AnnotationSetStatus({ binding }: { binding: AnnotationStudioBinding }) {
  const error =
    binding.setAction.error ??
    (binding.selectedSetLoad.status === "error" ? binding.selectedSetLoad.error : null);

  if (binding.sets.status === "error") {
    return (
      <EmptyState
        size="sm"
        tone="error"
        action={{ label: "다시 시도", onClick: () => void binding.refreshSets() }}
      >
        {binding.sets.error}
      </EmptyState>
    );
  }
  if (error !== null) {
    return (
      <span className={styles.error} title={error}>
        {error}
      </span>
    );
  }
  const downloadStatus = annotationSetDownloadStatus(binding.download);
  if (downloadStatus !== null) return downloadStatus;
  if (binding.selectedSetLoad.status === "loading") {
    return (
      <span className={`${styles.status} ${styles.statusLoading}`} role="status">
        <Spinner size="sm" className={inlineSpinnerClassName} />
        저장된 Annotation frame index를 불러오는 중입니다…
      </span>
    );
  }

  const materialize = binding.selectedSet?.materialize;
  const label = materialize == null ? "" : materializeLabel(materialize);
  return label === "" ? null : (
    <span className={styles.status} data-status={materialize?.status}>
      {label}
    </span>
  );
}

/** 세트 파일 다운로드의 진행·실패 한 줄. 알릴 것이 없으면 null 이라 렌더 상태 줄에 자리를 넘긴다. */
function annotationSetDownloadStatus(
  download: AnnotationSetDownloadController,
): ReactElement | null {
  const label = download.setLabel ?? "Annotation Set";
  switch (download.phase) {
    case "preparing":
      return (
        <span className={`${styles.status} ${styles.statusLoading}`} role="status">
          <Spinner size="sm" className={inlineSpinnerClassName} />
          {`${label} 파일 목록을 확인하는 중입니다…`}
        </span>
      );
    case "downloading": {
      const { total, completed, skipped } = download.progress;
      return (
        <span className={styles.status} role="status">
          {`${label} 파일 ${completed + skipped}/${total} 내려받는 중`}
          <button type="button" onClick={download.cancel}>
            중단
          </button>
        </span>
      );
    }
    case "error":
      return (
        <span className={styles.error} title={download.errorMessage ?? undefined}>
          {download.errorMessage}
          <button type="button" onClick={download.dismiss}>
            닫기
          </button>
        </span>
      );
    case "done":
      if (download.cancelled || download.failures.length === 0) return null;
      return (
        <span className={styles.error}>
          {`${label} 파일 ${download.failures.length}개를 내려받지 못했습니다.`}
          <button type="button" onClick={download.retryFailed}>
            다시 시도
          </button>
          <button type="button" onClick={download.dismiss}>
            닫기
          </button>
        </span>
      );
    case "idle":
      return null;
  }
}

/** 다운로드를 할 수 없는 사유를 돌려준다. 받을 수 있으면 null 이다. */
function downloadBlockReason(set: AnnotationSetSummaryView, active: boolean): string | null {
  if (!supportsDirectoryDownload()) return "Chrome 또는 Edge에서 내려받을 수 있습니다.";
  if (active) return "파일을 내려받는 중입니다.";
  return annotationSetBlockReason(set);
}

function frameSummary(set: { annotated_frame_count: number }): string {
  return set.annotated_frame_count === 0
    ? ""
    : ` · Annotation 프레임 ${set.annotated_frame_count}개`;
}

function materializeLabel(
  materialize: NonNullable<NonNullable<AnnotationStudioBinding["selectedSet"]>["materialize"]>,
) {
  switch (materialize.status) {
    case "queued":
      return "keyframe 렌더 대기";
    case "running":
      return `keyframe 렌더 중 ${materialize.rendered_count}/${materialize.requested_count}`;
    case "succeeded":
      return "";
    case "failed":
      return "keyframe 렌더 실패";
  }
}
